import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Archive, Loader2, Plus } from 'lucide-react';
import { CHEMINS, ficheDuCentre } from '../../../lib/navigation/chemins';
import { classesParCentre, lireCentres, lireEcoles } from '../../../lib/centres/gateway.supabase';
import { DESCRIPTION_DU_STATUT, NOM_DU_STATUT, type Centre, type StatutDeCentre } from '../../../lib/centres/regles';

/*
  Les centres : les lieux où Éclosion organise ses stages et ses journées
  pédagogiques. Un stage ne se programme que dans un centre de vacances ; une
  journée pédagogique, partout (VALIDÉ ÉCLOSION, 20/10/2026).

  La liste se lit. On crée un centre et on le modifie dans sa fiche — usage,
  école des élèves et classes comprises — où il s'archive ou se supprime.
*/

const COULEUR_DU_STATUT: Record<StatutDeCentre, string> = {
  actif: 'bg-green-100 text-green-800',
  pause: 'bg-amber-100 text-amber-900',
  test: 'bg-gray-200 text-gray-700',
};

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Brussels' })
    .format(new Date(iso));

export default function AdminCentresPage() {
  const [centres, setCentres] = useState<Centre[]>([]);
  const [ecoles, setEcoles] = useState<Map<string, string>>(new Map());
  const [classes, setClasses] = useState<Map<string, number>>(new Map());
  const [voirArchives, setVoirArchives] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    void (async () => {
      try {
        const [lus, ecolesLues] = await Promise.all([lireCentres(true), lireEcoles()]);
        const nombres = await classesParCentre(lus.map((c) => c.id));
        if (annule) return;
        setCentres(lus);
        setEcoles(new Map(ecolesLues.map((e) => [e.id, e.nom])));
        setClasses(nombres);
      } catch (e) {
        if (!annule) setErreur(e instanceof Error ? e.message : String(e));
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => { annule = true; };
  }, []);

  const archives = centres.filter((c) => c.archive);
  const visibles = voirArchives ? centres : centres.filter((c) => !c.archive);

  if (chargement) {
    return <div className="flex justify-center items-center min-h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Centres</h1>
          <p className="text-gray-600 mt-1">
            Les lieux d’Éclosion. Les stages se programment dans les centres de vacances ; les journées pédagogiques, partout.
          </p>
        </div>
        <Link to={CHEMINS.planification.centreNouveau} className="btn-primary flex items-center">
          <Plus className="h-5 w-5 mr-2" aria-hidden="true" />
          Nouveau centre
        </Link>
      </div>

      {erreur && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-2" role="alert">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>{erreur}</p>
        </div>
      )}

      {archives.length > 0 && (
        <label className="flex items-center justify-end gap-2 text-sm text-gray-700 min-h-[44px]">
          <input type="checkbox" checked={voirArchives} onChange={(e) => setVoirArchives(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Voir aussi les centres archivés ({archives.length})
        </label>
      )}

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700">
              <tr>
                <th className="px-4 py-3 font-semibold">Centre</th>
                <th className="px-4 py-3 font-semibold">Adresse</th>
                <th className="px-4 py-3 font-semibold">On y programme</th>
                <th className="px-4 py-3 font-semibold">École des élèves</th>
                <th className="px-4 py-3 font-semibold text-right">Classes</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((c) => (
                <tr key={c.id} className={`border-t border-gray-100 ${c.archive ? 'bg-amber-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    <Link to={ficheDuCentre(c.id)} className="font-semibold text-primary-700 hover:underline">{c.nom}</Link>
                    <span className="block text-xs text-gray-600">Code {c.code}</span>
                    {c.archive && (
                      <span className="flex items-center gap-1 text-xs text-amber-800 mt-0.5">
                        <Archive className="h-3 w-3" aria-hidden="true" />
                        Archivé{c.archiveLe ? ` le ${formatDate(c.archiveLe)}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {c.adresse}
                    <span className="block text-gray-600">{c.codePostal} {c.localite}</span>
                  </td>
                  <td className="px-4 py-3">
                    {c.centreDeVacances === null ? (
                      <span className="text-gray-500">—</span>
                    ) : c.centreDeVacances ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">Stages et journées pédagogiques</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-900">Journées pédagogiques</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{c.ecoleId ? ecoles.get(c.ecoleId) ?? '—' : '—'}</td>
                  <td className="px-4 py-3 text-right">{classes.get(c.id) ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${COULEUR_DU_STATUT[c.statut]}`} title={DESCRIPTION_DU_STATUT[c.statut]}>
                      {NOM_DU_STATUT[c.statut]}
                    </span>
                  </td>
                </tr>
              ))}
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-600">Aucun centre.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
